import { createContext, useContext, useEffect, useId } from "react";

export type CardDialogValue = {
  // The backend gives the setup session to the team admin only (403 for
  // the rest): members are told to ask the admin instead.
  canAddCard: boolean;
  openCardDialog: () => void;
  isOpeningCardDialog: boolean;
  // Back from Stripe, the saved card awaits its webhook: no second card
  // meanwhile (two could land out of order), the card buttons wait.
  isAwaitingCard: boolean;
  // Watchers outside the dialog (billing) tell whether they still wait.
  reportCardWait: (source: string, isWaiting: boolean) => void;
};

export const CardDialogContext = createContext<CardDialogValue | null>(null);

/** The app's card dialog, from CardDialogProvider. */
export const useCardDialog = () => {
  const ctx = useContext(CardDialogContext);

  if (!ctx) {
    throw new Error("useCardDialog must be used within CardDialogProvider");
  }

  return ctx;
};

/**
 * Holds the dialog's `isAwaitingCard` while `isWaiting`, until unmount.
 * Each call reports on its own; `source` only labels it.
 */
export const useCardWaitReport = (source: string, isWaiting: boolean) => {
  const { reportCardWait } = useCardDialog();
  const key = `${source}${useId()}`;

  useEffect(() => {
    reportCardWait(key, isWaiting);

    return () => reportCardWait(key, false);
  }, [key, isWaiting, reportCardWait]);
};
